// Import Head from vite-react-ssg, not <Helmet> from react-helmet-async
// directly: vite-react-ssg depends on its own nested react-helmet-async copy
// (it pins ^1.3.0; this project's top-level copy is a different install), and
// its build-time head extraction only reads state from THAT copy. Importing
// react-helmet-async directly gives every <Helmet> a Context object from the
// wrong copy, so its data never reaches the extractor — the tags still render,
// just inline in the body instead of the <head> vite-react-ssg produces.
import { Head } from "vite-react-ssg";
import {
  SITE_URL,
  DEFAULT_IMAGE,
  PHONE_E164,
  ADDRESS_LOCALITY,
  ADDRESS_REGION,
} from "../lib/site";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export function Seo({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
}: SeoProps) {
  const url = `${SITE_URL}${path}`;
  const schema = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {schema.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Head>
  );
}

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "P2A Marketing & Branding",
  url: SITE_URL,
  logo: DEFAULT_IMAGE,
  image: DEFAULT_IMAGE,
  description:
    "Consultoria estratégica e operação especializada em marketing, branding e automações com I.A. para infoprodutos e negócios físicos.",
  telephone: PHONE_E164,
  email: "contato@p2amarketing.com.br",
  address: {
    "@type": "PostalAddress",
    addressLocality: ADDRESS_LOCALITY,
    addressRegion: ADDRESS_REGION,
    addressCountry: "BR",
  },
  areaServed: "BR",
  sameAs: [
    "https://www.instagram.com/p2amarketing/",
    "https://www.facebook.com/profile.php?id=100084326275760",
    "https://www.tiktok.com/@pri_cboaventura",
  ],
};
